import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Import from refactored modules
import { LightboxProvider } from "@/context/LightboxContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { Sidebar, MobileHeader } from "@/components/layout";
import { API } from "@/utils/constants";
import { LoginPage, RequestAccessPage } from "@/components/auth";
import { UserManagementSection } from "@/components/users";
import { ServiceRequestSection } from "@/components/services";
import { SettingsSection } from "@/components/settings";
import { ReportsSection } from "@/components/reports";
import { Assets } from "@/components/assets";
import { DVIRSection } from "@/components/dvir";
import { InventoryModule } from "@/components/inventory";
import { HelpCenter } from "@/components/help";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

// Main App
function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authView, setAuthView] = useState("login"); // login, request-access
  const [activeSection, setActiveSection] = useState("assets");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [openServiceRequestForm, setOpenServiceRequestForm] = useState(false);
  const [openDVIRForm, setOpenDVIRForm] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [activeServiceRequestsCount, setActiveServiceRequestsCount] = useState(0);
  const [newPartsRequestsCount, setNewPartsRequestsCount] = useState(0);
  const [stats, setStats] = useState({
    tractors: 0,
    tractors_oos: 0,
    chassis: 0,
    chassis_oos: 0,
    yard_equipment: 0,
    yard_equipment_oos: 0,
    needs_bit: 0,
    needs_dot: 0,
    needs_oil: 0,
  });
  const [serviceIntervals, setServiceIntervals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check for saved user on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("fleetUser");
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem("fleetUser");
      }
    }
    // Seed default admin
    axios.post(`${API}/auth/seed-admin`).catch(() => {});
  }, []);

  // Fetch pending requests count for notification
  useEffect(() => {
    const isAdmin = currentUser?.role?.toLowerCase().includes("admin");
    if (isAdmin) {
      const fetchPendingCount = async () => {
        try {
          const response = await axios.get(`${API}/access-requests/pending-count`);
          setPendingRequestsCount(response.data.count);
        } catch (err) {
          console.error("Failed to fetch pending count", err);
        }
      };
      fetchPendingCount();
      // Refresh every 30 seconds
      const interval = setInterval(fetchPendingCount, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Fetch active service requests count
  useEffect(() => {
    if (currentUser) {
      const fetchActiveServiceRequests = async () => {
        try {
          const response = await axios.get(`${API}/repair-requests/active-count`);
          setActiveServiceRequestsCount(response.data.count);
        } catch (err) {
          console.error("Failed to fetch active service requests count", err);
        }
      };
      fetchActiveServiceRequests();
      // Refresh every 30 seconds
      const interval = setInterval(fetchActiveServiceRequests, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Fetch new parts requests count
  useEffect(() => {
    if (currentUser) {
      const fetchNewPartsRequests = async () => {
        try {
          const response = await axios.get(`${API}/parts-requests/counts`);
          setNewPartsRequestsCount(response.data.new || 0);
        } catch (err) {
          console.error("Failed to fetch parts requests count", err);
        }
      };
      fetchNewPartsRequests();
      // Refresh every 30 seconds
      const interval = setInterval(fetchNewPartsRequests, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const handleLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem("fleetUser", JSON.stringify(user));
    setAuthView("login");
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem("fleetUser");
    setActiveSection("assets");
  };

  // Refresh current user data (after profile update)
  const refreshCurrentUser = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const response = await axios.get(`${API}/users/${currentUser.id}`);
      const updatedUser = response.data;
      setCurrentUser(updatedUser);
      localStorage.setItem("fleetUser", JSON.stringify(updatedUser));
    } catch (error) {
      console.error("Failed to refresh user data", error);
    }
  }, [currentUser?.id]);

  const fetchData = useCallback(async () => {
    try {
      const [vehiclesRes, statsRes, intervalsRes, usersRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/service-intervals`),
        axios.get(`${API}/users`),
      ]);
      setVehicles(vehiclesRes.data);
      setStats(statsRes.data);
      setServiceIntervals(intervalsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, []);

  const seedIntervals = useCallback(async () => {
    try {
      await axios.post(`${API}/seed-intervals`);
    } catch (error) {
      console.error("Failed to seed intervals", error);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      seedIntervals().then(fetchData);
    }
  }, [seedIntervals, fetchData, currentUser]);

  // Show login/request access pages if not authenticated
  if (!currentUser) {
    if (authView === "request-access") {
      return (
        <RequestAccessPage 
          onBack={() => setAuthView("login")}
          onSuccess={() => setAuthView("login")}
        />
      );
    }
    return (
      <LoginPage 
        onLogin={handleLogin}
        onRequestAccess={() => setAuthView("request-access")}
      />
    );
  }

  const sectionTitles = {
    assets: "Assets",
    dvir: "DVIR",
    "service-request": "Service Request",
    reports: "Reports",
    settings: "Settings",
    users: "Users"
  };

  return (
    <div className="app-container">
      <Toaster position="bottom-center" richColors duration={1000} />
      <Sidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentUser={currentUser}
        onLogout={handleLogout}
        pendingRequestsCount={pendingRequestsCount}
        activeServiceRequestsCount={activeServiceRequestsCount}
        newPartsRequestsCount={newPartsRequestsCount}
        onUserUpdate={refreshCurrentUser}
      />
      <MobileHeader 
        onMenuClick={() => setSidebarOpen(true)} 
        title={sectionTitles[activeSection]}
        pendingRequestsCount={currentUser?.role?.toLowerCase().includes("admin") ? pendingRequestsCount : 0}
      />
      <main className="main-content">
        {activeSection === "assets" && (
          <Assets 
            vehicles={vehicles} 
            stats={stats} 
            serviceIntervals={serviceIntervals}
            onUpdate={fetchData}
            loading={loading}
            currentUser={currentUser}
            onNavigateToServiceRequest={() => {
              setActiveSection("service-request");
              setOpenServiceRequestForm(true);
            }}
            onNavigateToDVIR={() => {
              setActiveSection("dvir");
              setOpenDVIRForm(true);
            }}
          />
        )}
        {activeSection === "dvir" && (
          <DVIRSection 
            vehicles={vehicles}
            currentUser={currentUser}
            autoOpenForm={openDVIRForm}
            onFormOpened={() => setOpenDVIRForm(false)}
            onUpdate={fetchData}
          />
        )}
        {activeSection === "service-request" && (
          <ServiceRequestSection 
            vehicles={vehicles}
            autoOpenForm={openServiceRequestForm}
            onFormOpened={() => setOpenServiceRequestForm(false)}
            onUpdate={fetchData}
            currentUser={currentUser}
          />
        )}
        {activeSection === "parts" && (
          <InventoryModule 
            currentUser={currentUser}
            vehicles={vehicles}
          />
        )}
        {activeSection === "reports" && (
          <ReportsSection 
            vehicles={vehicles}
            serviceIntervals={serviceIntervals}
            users={users}
            currentUser={currentUser}
          />
        )}
        {activeSection === "settings" && (
          <SettingsSection 
            serviceIntervals={serviceIntervals}
            onUpdate={fetchData}
            currentUser={currentUser}
          />
        )}
        {activeSection === "users" && hasPermission(currentUser?.role, PERMISSIONS.NAV.USERS) && (
          <UserManagementSection currentUser={currentUser} />
        )}
        {activeSection === "help" && (
          <HelpCenter />
        )}
      </main>
    </div>
  );
}

// Wrap App with LightboxProvider
function AppWithProviders() {
  return (
    <LanguageProvider>
      <LightboxProvider>
        <App />
      </LightboxProvider>
    </LanguageProvider>
  );
}

export default AppWithProviders;
